<script>
// Seletor de país com busca: são 242 opções, e um <select> nativo obriga a
// rolar a lista inteira até achar o país.
import countries from 'shared/constants/countries';

export default {
  props: {
    modelValue: { type: String, default: 'BR' },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    emptyText: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      query: '',
      isOpen: false,
      highlighted: 0,
    };
  },
  computed: {
    options() {
      // Brasil primeiro (é a maioria dos cadastros), o resto em ordem alfabética.
      const rest = countries
        .filter(c => c.id !== 'BR')
        .sort((a, b) => a.name.localeCompare(b.name));
      return [countries.find(c => c.id === 'BR'), ...rest].filter(Boolean);
    },
    selected() {
      return this.options.find(c => c.id === this.modelValue);
    },
    // Busca pelo nome ou pela sigla: quem sabe o código digita "PT" e acha
    // Portugal sem passar por Paraguai e Peru.
    filtered() {
      const term = this.query.trim().toLowerCase();
      if (!term) return this.options;
      return this.options.filter(
        c =>
          c.name.toLowerCase().includes(term) ||
          c.id.toLowerCase() === term ||
          c.dial_code.includes(term)
      );
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    onDocumentClick(event) {
      if (this.isOpen && !this.$el.contains(event.target)) this.close();
    },
    open() {
      this.isOpen = true;
      this.query = '';
      this.highlighted = 0;
      this.$nextTick(() => this.$refs.search?.focus());
    },
    close() {
      this.isOpen = false;
      this.query = '';
    },
    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    },
    pick(country) {
      this.$emit('update:modelValue', country.id);
      this.close();
      this.$refs.trigger?.focus();
    },
    move(step) {
      if (!this.filtered.length) return;
      const next = this.highlighted + step;
      this.highlighted = Math.min(Math.max(next, 0), this.filtered.length - 1);
      this.$nextTick(() => {
        this.$refs.list
          ?.querySelectorAll('[data-option]')
          ?.[this.highlighted]?.scrollIntoView({ block: 'nearest' });
      });
    },
    pickHighlighted() {
      const country = this.filtered[this.highlighted];
      if (country) this.pick(country);
    },
  },
};
</script>

<template>
  <div class="relative">
    <label v-if="label" class="block mb-1 text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>

    <button
      ref="trigger"
      type="button"
      class="flex items-center justify-between w-full px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-background text-n-slate-12"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="toggle"
      @keydown.down.prevent="open"
    >
      <span v-if="selected" class="truncate">
        {{ selected.emoji }} {{ selected.name }}
      </span>
      <span v-else class="text-n-slate-10">{{ placeholder }}</span>
      <span
        class="flex-none ml-2 i-lucide-chevron-down size-4 text-n-slate-10"
      />
    </button>

    <div
      v-if="isOpen"
      class="absolute z-50 w-full mt-1 border rounded-lg shadow-lg border-n-weak bg-n-solid-1"
    >
      <div class="p-2 border-b border-n-weak">
        <input
          ref="search"
          v-model="query"
          type="text"
          class="w-full px-2 py-1.5 text-sm rounded-md reset-base border border-n-weak bg-n-background text-n-slate-12"
          :placeholder="placeholder"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="pickHighlighted"
          @keydown.esc.prevent="close"
        />
      </div>
      <ul ref="list" class="py-1 overflow-y-auto max-h-60" role="listbox">
        <li
          v-for="(country, index) in filtered"
          :key="country.id"
          data-option
          role="option"
          :aria-selected="country.id === modelValue"
          class="px-3 py-1.5 text-sm cursor-pointer text-n-slate-12"
          :class="index === highlighted ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-1'"
          @click="pick(country)"
          @mouseenter="highlighted = index"
        >
          {{ country.emoji }} {{ country.name }}
        </li>
        <li v-if="!filtered.length" class="px-3 py-2 text-sm text-n-slate-10">
          {{ emptyText }}
        </li>
      </ul>
    </div>
  </div>
</template>
